import { createSignal, onCleanup, onMount } from 'solid-js';
import {
  randomSquare,
  squaresAtKnightMoves,
} from '@domain/chess/knightReach';
import type { Square } from '@domain/chess/types';
import { recordModeAttempt } from '@state/modeStore';
import {
  beginMarkPuzzle,
  endMarkPuzzle,
  setOrientation,
  setRevealMarks,
} from '@state/actions';
import { gameStore } from '@state/gameStore';
import './HorseRideQuiz.css';

export type HorseMoveCount = 1 | 2 | 3 | 4;

function newStart(exclude?: Square): Square {
  let sq = randomSquare();
  for (let i = 0; i < 16 && sq === exclude; i++) sq = randomSquare();
  return sq;
}

export function HorseRideQuiz() {
  const [moves, setMoves] = createSignal<HorseMoveCount>(1);
  const [start, setStart] = createSignal<Square>(newStart());
  const [revealed, setRevealed] = createSignal(false);

  function loadPuzzle(nextMoves: HorseMoveCount, nextStart?: Square) {
    const sq = nextStart ?? newStart(start());
    setMoves(nextMoves);
    setStart(sq);
    setRevealed(false);
    setOrientation('white');
    beginMarkPuzzle([{ square: sq, color: 'w', role: 'n' }]);
  }

  onMount(() => {
    loadPuzzle(1, start());
  });

  onCleanup(() => {
    endMarkPuzzle();
  });

  function onReveal() {
    const solution = new Set(squaresAtKnightMoves(start(), moves()));
    const marked = new Set(gameStore.markedSquares);
    const marks: { sq: Square; mark: 'correct' | 'wrong' | 'missing' }[] = [];
    for (const sq of marked) {
      marks.push({ sq, mark: solution.has(sq) ? 'correct' : 'wrong' });
    }
    for (const sq of solution) {
      if (!marked.has(sq)) marks.push({ sq, mark: 'missing' });
    }
    const matched =
      marked.size === solution.size && [...marked].every((sq) => solution.has(sq));
    recordModeAttempt('horse-ride', matched);
    setRevealMarks(marks);
    setRevealed(true);
  }

  function onNext() {
    loadPuzzle(moves());
  }

  function onMovesChange(value: string) {
    const n = Number(value) as HorseMoveCount;
    if (n !== 1 && n !== 2 && n !== 3 && n !== 4) return;
    loadPuzzle(n);
  }

  return (
    <>
      <section class="game-rules" aria-label="Game rules">
        <p>
          Find every square the knight can reach in exactly that many moves. Mark the squares,
          then reveal the solution. A new puzzle loads if you change the move count.
        </p>
      </section>
      <div class="horse-ride__toolbar">
        <select
          class="opening-search__moves"
          aria-label="Number of knight moves"
          value={String(moves())}
          onChange={(e) => onMovesChange(e.currentTarget.value)}
        >
          <option value="1">1 move</option>
          <option value="2">2 moves</option>
          <option value="3">3 moves</option>
          <option value="4">4 moves</option>
        </select>
        {revealed() ? (
          <button type="button" class="btn horse-ride__reveal" onClick={onNext}>
            Next
          </button>
        ) : (
          <button type="button" class="btn horse-ride__reveal" onClick={onReveal}>
            Reveal
          </button>
        )}
      </div>
    </>
  );
}
